import { Box, ButtonGroup, IconButton, Tooltip } from "@chakra-ui/react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { t } from "@lingui/macro";
import { PlanningDailyViewWhereColumn } from "@src/__generated__/graphql";
import {
  AllocationsColumnUserHeader,
  type AllocationsColumnUserHeaderProps,
} from "@src/components/modules/resource-planning/timeline/AllocationsColumnUserHeader";
import { useResourcePlanningDailyViewStore } from "@src/components/modules/resource-planning/timeline/ResourcePlanningDayPage";
import { DragHandle } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { useScreenType } from "@src/utils/hooks/useIsMobile";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import type { CSSProperties, FC } from "react";

type SortableItemProps = AllocationsColumnUserHeaderProps;

export const SortableUserHeader: FC<SortableItemProps> = observer(
  function SortableUserHeader(props) {
    const { user } = props;
    const resourcePlanningStore = useResourcePlanningDailyViewStore();
    const { workspaceStore, editUserModalStore } = useStore();
    const { isDesktop } = useScreenType();

    const isUserSortingDisabled = computed(() => {
      if (!isDesktop) return false;

      return resourcePlanningStore.where.filters.some(({ value, column }) => {
        const isTeamFilter = column === PlanningDailyViewWhereColumn.TeamId;

        if (Array.isArray(value)) {
          return isTeamFilter ? value.length > 1 : value.length > 0;
        }

        return !!value;
      });
    }).get();

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: user.id,
      data: {
        user,
        type: "user",
      },
      disabled: isUserSortingDisabled,
      // TODO: add `satisfies DragUser` when we will have support for `satisfies`
    });

    const containerStyle: CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 1 : 0,
    };

    const headerStyle: CSSProperties = {
      borderWidth: isDragging ? "1px" : undefined,
      borderColor: isDragging ? "grey.200" : undefined,
    };

    return (
      <Box ref={setNodeRef} sx={containerStyle} {...attributes} role="group">
        <Box pos="relative">
          <AllocationsColumnUserHeader {...props} sx={headerStyle} />
          <ButtonGroup
            pos="absolute"
            top="1"
            right="1"
            bg="white"
            _groupHover={{ visibility: "visible" }}
            visibility="hidden"
            colorScheme="grey"
            size="sm"
            spacing="0"
            variant="ghost"
          >
            {can("user_update_all") && (
              <IconButton
                aria-label={t`Edit user`}
                icon={<Icon name="edit-02" />}
                onClick={() => {
                  editUserModalStore.modalState.onOpen({
                    userId: user.id,
                    userType: "internal",
                    onSubmit: () => resourcePlanningStore.syncData(),
                  });
                }}
              />
            )}
            {!workspaceStore.settings?.planning_alphabet_user_order &&
              isDesktop && (
                <Tooltip
                  isDisabled={!isUserSortingDisabled}
                  label={t`Unable to reorder users with filters enabled.`}
                >
                  <IconButton
                    {...listeners}
                    aria-label={t`Drag to reorder`}
                    icon={<DragHandle />}
                    isDisabled={isUserSortingDisabled}
                  />
                </Tooltip>
              )}
          </ButtonGroup>
        </Box>
      </Box>
    );
  },
);
